// src/pages/admin/UserList.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ExcelDownloadButton from '../../components/common/ExcelDownloadButton';
import { getUserListWithRolesExportColumns } from '../../utils/userListExport';
import API from '../../api';
import UserLink from '../../components/common/UserLink'; // Import UserLink
import { formatFullName } from '../../utils/name';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import UserStatusBadge from '../../components/common/UserStatusBadge';
import ListPageLayout, { ListPageHeader, ListTableCard } from '../../components/layout/ListPageLayout';
import PaginationFooter from '../../components/common/PaginationFooter';
import SortableTableHeader from '../../components/common/SortableTableHeader';
import useSortedClientPagination from '../../hooks/useSortedClientPagination';
import { userListSortColumns } from '../../config/tableSortColumns';

const UserList = () => {
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const navigate = useNavigate();
    const pagination = useSortedClientPagination(users, { columns: userListSortColumns });

    useEffect(() => {
        setLoading(true);
        setLoadError(false);
        API.get('/admin/users')
            .then(response => {
                setUsers(Array.isArray(response.data) ? response.data : (response.data?.content || []));
                setLoadError(false);
            })
            .catch(() => {
                setUsers([]);
                setLoadError(true);
            })
            .finally(() => setLoading(false));
    }, []);

    const RoleBadges = ({ roles }) => (
        <div className="flex gap-1 justify-center flex-wrap">
            {(roles || []).map(r => (
                <Badge key={r} variant="gray">{t(`faq.roles.${r}`, { defaultValue: r })}</Badge>
            ))}
        </div>
    );

    return (
        <ListPageLayout>
            <ListPageHeader
                title={t('pages.users.title')}
                subtitle={t('pages.users.subtitle')}
                actions={(
                    <>
                        <ExcelDownloadButton
                            columns={getUserListWithRolesExportColumns(t)}
                            data={users}
                            filename="users"
                            disabled={users.length === 0}
                        />
                        <button
                            onClick={() => navigate('/admin/users/new')}
                            className="btn-page-action-primary"
                        >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        {t('pages.users.add')}
                    </button>
                    </>
                )}
            />

            {loadError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
                    {t('common.listLoadFailed')}
                </div>
            )}
            <ListTableCard>
                <div className="overflow-x-auto">
                    <table className="min-w-full">
                        <thead className="table-header">
                            <tr>
                                <SortableTableHeader label={t('common.fullName')} columnKey="name" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                <SortableTableHeader label={t('common.username')} columnKey="username" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                <SortableTableHeader label={t('common.email')} columnKey="email" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                <SortableTableHeader label={t('common.phone')} columnKey="phone" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                <th className="table-header-cell">{t('common.roles')}</th>
                                <SortableTableHeader label={t('common.status')} columnKey="status" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                <th className="table-header-cell">{t('common.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading && (
                                <tr><td colSpan={7} className="table-cell text-center text-gray-500 py-8">{t('common.loading')}</td></tr>
                            )}
                            {!loading && loadError && (
                                <tr><td colSpan={7} className="table-cell text-center text-rose-700 py-8">{t('common.listLoadFailed')}</td></tr>
                            )}
                            {!loading && !loadError && pagination.pageItems.length === 0 && (
                                <tr><td colSpan={7} className="table-cell text-center text-gray-500 py-8">{t('common.noData', { defaultValue: 'No users found.' })}</td></tr>
                            )}
                            {pagination.pageItems.map(u => (
                                <tr key={u.id} className="table-row">
                                    <td className="table-cell">
                                        {formatFullName(u.firstName, u.middleName, u.lastName) ? (
                                            <UserLink
                                                id={u.id}
                                                name={formatFullName(u.firstName, u.middleName, u.lastName)}
                                            />
                                        ) : (
                                            <span className="text-gray-400">—</span>
                                        )}
                                    </td>
                                    <td className="table-cell">{u.username || <span className="text-gray-400">—</span>}</td>
                                    <td className="table-cell">{u.email || <span className="text-gray-400">-</span>}</td>
                                    <td className="table-cell">{u.phoneNumber || <span className="text-gray-400">-</span>}</td>
                                    <td className="table-cell">
                                        <div className="flex gap-1 justify-start flex-wrap">
                                            <RoleBadges roles={u.roles} />
                                        </div>
                                    </td>
                                    <td className="table-cell">
                                        <UserStatusBadge active={u.active} />
                                    </td>
                                    <td className="table-cell whitespace-nowrap">
                                        <button
                                            onClick={() => navigate(`/admin/users/${u.id}`)}
                                            className="btn-action-edit"
                                        >
                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                            {t('common.edit')}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {pagination.total > 0 && (
                    <PaginationFooter
                        page={pagination.page}
                        size={pagination.size}
                        total={pagination.total}
                        onPageChange={pagination.setPage}
                        onSizeChange={pagination.setSize}
                    />
                )}
            </ListTableCard>
        </ListPageLayout>
    );
};

export default UserList;
